// Delete-action guardrails — the pure, DB-free core of the admin-only
// "Delete project" / "Delete account" affordances (CEO walkthrough note
// 2026-07-29: "How do I delete a project, or an account? I don't see an
// option.").
//
// Shared on purpose (same discipline as po-preconditions / order-money):
//   - the CLIENT reads these to decide who SEES the action (admin only), when
//     it's PRE-DISABLED with an honest reason (round-47 honest-gate pattern —
//     an account with active sub-accounts can't be deleted, so we say so up
//     front instead of click-then-409), which server refusal message to SURFACE
//     verbatim, whether the destructive call MAY fire (confirm-before-delete),
//     and where to navigate on success (list route + flash);
//   - the SERVER keeps the authoritative backstop (requireRole("admin") on
//     DELETE /projects/:id + DELETE /clients/:id, and softDeleteClient's
//     AccountHasSubAccountsError 409);
//   - the offline QA gate (apps/api/src/db/qa-delete-actions.ts) EXECUTES these
//     same decisions against fixtures, so what the UI does and what the endpoint
//     enforces can never drift, and the gate goes red by name if any decision is
//     commented out or `false &&`-disabled.
//
// DB-free and dependency-free (no React, no ApiError import): the web component
// normalises whatever it holds into these small inputs.

/** The confirm-before-delete flow phase. `idle` = no dialog; `confirming` = the
 *  confirmation dialog is open (the ONLY state the destructive call may fire
 *  from); `deleting` = the DELETE request is in flight. */
export type DeletePhase = "idle" | "confirming" | "deleting";

/**
 * The HONEST recoverability line, shared by both delete dialogs (project +
 * account). There is NO in-app undo — a soft delete flips a `deleted_at` flag,
 * and restoring it is a manual database fix by engineering. Deliberately avoids
 * the word "operator" (implies a self-serve in-app action that doesn't exist)
 * and never promises a one-click restore.
 */
export const DELETE_RECOVERABILITY_NOTE =
  "This isn't permanent, but there's no in-app undo — recovering it means asking engineering to restore it directly.";

/** The committed-document facts a project carries, read straight off the data
 *  the project page already fetched (stage-node counts — no new endpoint). */
export interface ProjectDeleteDocContext {
  /** A real (post-quote, non-cancelled) Quote exists — the sales_order
   *  stage-node count is > 0. */
  hasCommittedSalesOrder: boolean;
  /** Purchase orders across the project's orders (production stage-node count). */
  purchaseOrderCount: number;
  /** Invoices across the project's orders (invoice stage-node count). */
  invoiceCount: number;
}

/** Join a list of noun phrases with commas + a trailing "and" (Oxford). */
function joinAnd(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

/**
 * The doc-aware PROJECT delete confirmation body (the predicate — the caller
 * renders the bold project name as the subject in front of it). When the project
 * carries committed documents (a Quote / POs / invoices), name them
 * concretely and warn that downstream parties (vendors, the client) may already
 * hold those documents and can still act on them, while merch'd loses the
 * project. When it carries none, keep the plain copy. Always ends with the
 * honest recoverability note (no in-app undo).
 *
 * Pure so the offline gate EXECUTES the zero-doc vs with-docs branches against
 * fixtures — the copy can't silently regress to a generic warning.
 */
export function buildProjectDeleteBody(ctx: ProjectDeleteDocContext): string {
  const docs: string[] = [];
  if (ctx.hasCommittedSalesOrder) docs.push("a committed Quote");
  if (ctx.purchaseOrderCount > 0) {
    docs.push(
      `${ctx.purchaseOrderCount} purchase order${ctx.purchaseOrderCount === 1 ? "" : "s"}`,
    );
  }
  if (ctx.invoiceCount > 0) {
    docs.push(`${ctx.invoiceCount} invoice${ctx.invoiceCount === 1 ? "" : "s"}`);
  }

  if (docs.length === 0) {
    return `has no committed documents yet. Deleting it removes the project and its documents from your workspace. ${DELETE_RECOVERABILITY_NOTE}`;
  }

  return `has ${joinAnd(docs)}. Deleting it removes the project from your workspace — vendors and the client may already hold those documents and can still act on them, but no one at Other will be able to find this project afterward. ${DELETE_RECOVERABILITY_NOTE}`;
}

/**
 * Who may SEE a delete action: admins only. Mirrors the server's
 * requireRole("admin") on DELETE /projects/:id and DELETE /clients/:id — a
 * non-admin never sees the affordance AND would be refused at the endpoint.
 * Tolerant of a missing/absent roles list (logged-out / unresolved user).
 */
export function canSeeDeleteAction(
  roles: readonly string[] | null | undefined,
): boolean {
  return !!roles && roles.includes("admin");
}

// ─── Sent-document delete guard (round-53 follow-up, chip task_2575ab52) ──────
//
// The ux-reviewer's round-53 blocker: soft-deleting a project flips its
// `deleted_at` so it goes internally invisible, but the vendor/client PUBLIC
// links (the `/o/:uuid`, `/po/:uuid`, `/i/:uuid` share UUIDs) it already SENT
// stay LIVE. The delete UI named the documents but the SERVER route was
// untouched — an admin could soft-delete a project whose sent Quote, POs,
// and invoices are still openable by the outside world, with no record of the
// choice. This closes that gap server-side (409 refusal + audited admin
// override), single-sourced through this pure module so the endpoint and the
// offline gate can never drift.
//
// "SENT" is read off the real lifecycle timestamps (a live public link is out in
// the world only once the doc was actually sent):
//   - Quote → orders.sent_for_approval_at / orders.so_approved_at
//   - Purchase Order → purchase_orders.sent_at
//   - Invoice → invoices.sent_at
// scoped to the SAME committed-SO / live-PO row filters the round-53 UI counts
// use (post-quote non-cancelled SO; non-cancelled non-deleted PO). NOTE: this is
// deliberately a NARROWER quantity than the round-53 dialog's existence copy
// (which names any committed SO / PO / invoice, sent or not) — the copy is
// informational ("this project has documents"), the guard is the specific
// live-public-link risk ("these were SENT"). They are NOT the same count and are
// intentionally not merged: merging would either mislabel the copy or over-refuse
// on never-sent drafts.

/** The structured server code a project-delete refusal carries — surfaced
 *  verbatim client-side (round-52 pattern) and matched by the admin-override
 *  affordance. */
export const PROJECT_HAS_SENT_DOCUMENTS = "project_has_sent_documents";

/** The sent-document counts a project carries — a live public link is already in
 *  a vendor's or the client's hands for each. Classified row-by-row from the
 *  lifecycle facts by the pure predicates below (the SAME code the endpoint runs
 *  and the gate executes), so what counts as "live" can't drift. */
export interface ProjectSentDocCounts {
  /** Non-cancelled orders (estimate OR quote) with a live /o/:uuid client
   *  link — non-draft state (mirrors the route gate), sent for approval, or
   *  signed off. NOT gated to committed+ stages. */
  sentSalesOrderCount: number;
  /** Non-cancelled purchase orders with a live /po/:uuid vendor link — sent. */
  sentPurchaseOrderCount: number;
  /** Non-deleted invoices — the /i/:uuid link is live + PAYABLE from creation. */
  sentInvoiceCount: number;
}

// ─── Row-level public-link classifiers (pure, gate-executed) ─────────────────
//
// The predicate is derived from what the PUBLIC ROUTES actually gate on — NOT
// from the round-53 informational dialog's stage filter (code-review REJECT-1):
//   - GET /o/:uuid (getOrderByShareUuid) serves any non-deleted order; the route
//     shows it for any non-cancelled order — a sent-but-unconverted QUOTE-stage
//     estimate is fully public WITH an Approve action.
//   - GET /i/:uuid (getInvoiceByShareUuid) + POST /i/:uuid/pay gate ONLY on
//     isNull(deleted_at) — a converted-but-unsent invoice already has a live,
//     PAYABLE link.
// countSentDocuments SELECTs the candidate rows and classifies them through these
// functions, so the gate's fixture checks exercise the exact decision the server
// makes (source-lints alone were structurally blind to the stage/sent gaps).

/** Order lifecycle facts (timestamps are "stamped" when non-null). */
export interface OrderPublicLinkFacts {
  statusStage: string;
  statusState: string;
  sentForApprovalAt: unknown;
  soApprovedAt: unknown;
}
/**
 * Whether an order has a LIVE client-facing public link (/o/:uuid). Derived from
 * that route's ACTUAL gate — statusStage !== "cancelled" && statusState !==
 * "draft" — NOT from a proxy set of "sent" timestamps. A rep can convert a
 * NEVER-SENT estimate straight to a Quote (POST /orders/:id/convert-to-
 * sales-order), which lands it committed/approved = publicly viewable AND
 * approvable at /o/:uuid, yet stamps NEITHER sentForApprovalAt NOR soApprovedAt.
 * The statusState mirror catches that live-but-unstamped order. The timestamp OR
 * is belt-and-suspenders: a formerly-SENT order that regressed to a draft state
 * mid-production keeps refusing (safe over-refusal — a once-shared link may still
 * be out in the world). NOT gated to a stage set (a sent quote-stage estimate is
 * public + approvable).
 */
export function orderHasLiveClientLink(o: OrderPublicLinkFacts): boolean {
  return (
    o.statusStage !== "cancelled" &&
    (o.statusState !== "draft" ||
      o.sentForApprovalAt != null ||
      o.soApprovedAt != null)
  );
}

/** PO lifecycle facts. */
export interface PoPublicLinkFacts {
  internalStage: string;
  sentAt: unknown;
}
/** Whether a PO has a LIVE vendor-facing public link (/po/:uuid) — sent to the
 *  vendor and not cancelled. */
export function poHasLiveVendorLink(p: PoPublicLinkFacts): boolean {
  return p.internalStage !== "cancelled" && p.sentAt != null;
}

/** Invoice lifecycle facts. `sentAt` is part of the shape but deliberately
 *  IGNORED — see below. */
export interface InvoicePublicLinkFacts {
  sentAt: unknown;
}
/**
 * Whether an invoice has a LIVE public link. The public invoice route serves AND
 * the pay route charges with only a not-deleted gate — no sent/status gate today
 * — so a converted-but-UNSENT invoice already carries a live, PAYABLE link.
 * EXISTENCE is the guard: any non-deleted invoice counts (the caller filters
 * deleted rows). `sentAt` is intentionally unused. See chip task_2575ab52 for the
 * underlying /i/:uuid route-gate gap — NOT fixed in this branch.
 */
export function invoiceHasLiveLink(_i: InvoicePublicLinkFacts): boolean {
  return true;
}

/** The 409 body a sent-documents refusal returns. `detail` is the verbatim,
 *  client-surfaced sentence (extractDeleteErrorMessage reads it). */
export interface ProjectSentDocsRefusal {
  error: typeof PROJECT_HAS_SENT_DOCUMENTS;
  sentSalesOrderCount: number;
  sentPurchaseOrderCount: number;
  sentInvoiceCount: number;
  detail: string;
}

/**
 * The project-delete SENT-document decision: returns the structured 409 refusal
 * when the project carries any sent document (a live public link), else null
 * (delete proceeds). Names each sent document concretely and warns — honestly —
 * that deleting the project does NOT kill those public links. Pure so the offline
 * gate EXECUTES the refuse-vs-allow branches against fixtures and the endpoint
 * imports the exact same decision (no drift).
 */
export function projectSentDocsRefusal(
  counts: ProjectSentDocCounts,
): ProjectSentDocsRefusal | null {
  const docs: string[] = [];
  if (counts.sentSalesOrderCount > 0) {
    // "order" not "Quote" — the count now includes sent QUOTE-stage
    // estimates (also public + approvable), so the narrower noun would misname them.
    docs.push(
      `${counts.sentSalesOrderCount} order${counts.sentSalesOrderCount === 1 ? "" : "s"} sent to the client`,
    );
  }
  if (counts.sentPurchaseOrderCount > 0) {
    docs.push(
      `${counts.sentPurchaseOrderCount} purchase order${counts.sentPurchaseOrderCount === 1 ? "" : "s"} sent to vendors`,
    );
  }
  if (counts.sentInvoiceCount > 0) {
    docs.push(
      `${counts.sentInvoiceCount} invoice${counts.sentInvoiceCount === 1 ? "" : "s"}`,
    );
  }
  if (docs.length === 0) return null;
  // No "delete the documents first" remediation — quotes have no cancel
  // affordance in the UI and invoices have no delete route at all (only POs can
  // be cancelled), so it would point at a nonexistent action (ux SHOULD-FIX). The
  // override consequence lives in PROJECT_SENT_DOCS_OVERRIDE_NOTE, so the detail
  // stays short and states only the fact.
  const detail =
    `This project has ${joinAnd(docs)}. Those public links stay live even after the project is deleted — vendors and the client can still open them.`;
  return {
    error: PROJECT_HAS_SENT_DOCUMENTS,
    sentSalesOrderCount: counts.sentSalesOrderCount,
    sentPurchaseOrderCount: counts.sentPurchaseOrderCount,
    sentInvoiceCount: counts.sentInvoiceCount,
    detail,
  };
}

/** The HONEST consequence copy shown beside the admin "Delete anyway" override on
 *  the project delete dialog after a sent-documents refusal. Matches the shipped
 *  no-in-app-undo grammar: it does NOT pretend the public links get revoked, and
 *  it says the choice is logged (audited override). */
export const PROJECT_SENT_DOCS_OVERRIDE_NOTE =
  "Deleting anyway removes the project from your workspace, but the sent documents' public links stay live — vendors and the client can still open them. This override is logged.";

/**
 * Who may OVERRIDE the sent-documents guard: admins only — the SAME gate that
 * lets a viewer see the delete action at all. Mirrors the server backstop
 * (requireRole("admin") on DELETE /projects/:id, which the override flag rides on
 * the same request as). Exposed as its own predicate so the offline gate can
 * EXECUTE "a non-admin cannot override" independently of the see-the-action gate.
 */
export function mayOverrideSentDocsDelete(
  roles: readonly string[] | null | undefined,
): boolean {
  return canSeeDeleteAction(roles);
}

/**
 * Structural read of a failed-delete error's server `error` code (DB-free, no
 * ApiError import — same defensive shape as extractDeleteErrorMessage). Lets the
 * client decide whether to offer the admin override without re-deriving the
 * refusal. Returns the trimmed code string, or null when absent.
 */
export function deleteRefusalCode(err: unknown): string | null {
  const body =
    err && typeof err === "object" && "body" in err
      ? (err as { body?: unknown }).body
      : undefined;
  if (body && typeof body === "object") {
    const code = (body as { error?: unknown }).error;
    if (typeof code === "string" && code.trim()) return code.trim();
  }
  return null;
}

/**
 * The destructive DELETE may fire ONLY from the confirmed state. The trigger
 * button just OPENS the dialog (moves phase to `confirming`); the dialog's
 * confirm button is the sole path to the mutation, and it calls this guard
 * first. So a delete can never happen without an explicit confirmation step.
 */
export function mayFireDelete(phase: DeletePhase): boolean {
  return phase === "confirming";
}

/** The two counts that can block an account delete (server-authoritative;
 *  mirrored here for the pre-disable reason). */
export interface AccountDeleteCounts {
  /** Active (non-deleted) sub-accounts under this Account. */
  subAccountCount: number;
  /** Active (non-deleted) projects in this Account (CEO 2026-07-29). */
  activeProjectCount: number;
}

/**
 * Why an ACCOUNT delete is pre-disabled (honest gate), or null when deletable.
 * softDeleteClient REFUSES (409) in two cases, checked in a FIXED order so the
 * message names only the FIRST blocking condition (no stacked confusion):
 *   1. active sub-accounts (`account_has_sub_accounts`) — can't orphan children.
 *   2. active projects (`account_has_projects`, CEO 2026-07-29) — delete the
 *      projects first, then the account.
 * The wording mirrors the server's AccountHasSubAccountsError /
 * AccountHasProjectsError messages so the pre-disable reason and the 409 detail
 * read alike. Same check order as softDeleteClient (sub-accounts first).
 */
export function accountDeleteBlockReason(
  counts: AccountDeleteCounts,
): string | null {
  if (counts.subAccountCount > 0) {
    const noun = counts.subAccountCount === 1 ? "sub-account" : "sub-accounts";
    const them = counts.subAccountCount === 1 ? "it" : "them";
    return `This account has ${counts.subAccountCount} active ${noun}. Reassign or remove ${them} before deleting.`;
  }
  if (counts.activeProjectCount > 0) {
    const noun = counts.activeProjectCount === 1 ? "project" : "projects";
    return `This account still has ${counts.activeProjectCount} active ${noun}. Delete those projects first, then the account.`;
  }
  return null;
}

/**
 * The HONEST error message for a failed delete — never a generic "delete
 * failed". Pulls the server's own words: the `detail` sentence a refusal
 * carries (e.g. the AccountHasSubAccountsError message on a 409) takes
 * precedence; a bare `error` string that already reads as a sentence (contains
 * a space, e.g. "not found or already deleted") is surfaced capitalised; a raw
 * snake_case error code is NOT shown (it would leak jargon) — the caller's
 * entity-specific fallback is used instead.
 *
 * Accepts anything (an ApiError has `.body`); reads structurally so the shared
 * module stays free of the web ApiError import.
 */
export function extractDeleteErrorMessage(
  err: unknown,
  fallback: string,
): string {
  const body =
    err && typeof err === "object" && "body" in err
      ? (err as { body?: unknown }).body
      : undefined;
  if (body && typeof body === "object") {
    const b = body as { detail?: unknown; error?: unknown };
    if (typeof b.detail === "string" && b.detail.trim()) {
      return b.detail.trim();
    }
    if (typeof b.error === "string" && b.error.trim().includes(" ")) {
      const s = b.error.trim();
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
  }
  return fallback;
}

/** The navigation payload after a successful delete: the list route plus a
 *  flash message carried in router state (the list page shows it as a toast). */
export interface DeleteSuccessNav {
  to: string;
  state: { flash: string };
}

/**
 * Where to go after a successful delete: back to the entity's list, carrying a
 * confirmation flash in router state. The list page reads `location.state.flash`
 * and renders the toast. Pure so the gate can assert the redirect + flash it
 * builds without a browser.
 */
export function buildDeleteSuccessNav(
  redirectTo: string,
  flash: string,
): DeleteSuccessNav {
  return { to: redirectTo, state: { flash } };
}
